import { useEffect, useState } from 'react';

import { CheckCheck, Edit, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { TooltipButton } from '@/components/ui/tooltip-button';
import { useConfirmModal } from '@/hooks/useConfirmModal';
import { getUserInfo } from '@/lib/auth';
import { apiService } from '@/lib/axios';

import Checkbox from '../ui/checkbox';

export const tabList = [
  {
    key: 'information',
    title: 'Comments for Document Information',
  },
  {
    key: 'history',
    title: 'Comments for Document History & Approval',
  },
  {
    key: 'objectiveAndScope',
    title: 'Comments for Objective and Scope',
  },
  {
    key: 'terms',
    title: 'Comments for Terms & Definitions',
  },
  {
    key: 'references',
    title: 'Comments for References',
  },
  {
    key: 'responsibilities',
    title: 'Comments for Responsibilities',
  },
  {
    key: 'clauseDetails',
    title: 'Comments for Clause Details',
  },
  {
    key: 'appendix',
    title: 'Comments for Appendix',
  },
];

const formatDate = (value) => {
  if (!value) return '';

  const d = new Date(value);

  if (isNaN(d.getTime())) return '';

  const pad = (n) => String(n).padStart(2, '0');

  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
};

const CommentsPanel = ({
  activeTab,
  className = '',
  reviews,
  reviewerRole,
  periodReviewValue: providedPeriodReviewValue = 3,
  setCanApprove,
  onPeriodReviewChange,
  setReviews,
  entityId,
  entityRevision,
  entityType = 'Document',
  currentUserSub,
  canSubmit = true,
  tabs = tabList,
}) => {
  const currentUser = getUserInfo();
  const { showConfirmModal } = useConfirmModal();
  const effectiveCurrentUserSub = currentUserSub || currentUser?.sub;
  const userName = currentUser?.name || 'You';
  const [fieldValue, setFieldValue] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const savedCommentIds = new Set(
    reviews.filter((r) => r.entityId).map((r) => r.id)
  );
  const isDocument = entityType === 'Document';
  const [periodReviewValue, setPeriodReviewValue] = useState(
    providedPeriodReviewValue
  );

  useEffect(() => {
    setPeriodReviewValue(providedPeriodReviewValue);
  }, [providedPeriodReviewValue]);

  const handlePeriodReviewChange = (value) => {
    const years = parseInt(value, 10) || 3;

    setPeriodReviewValue(years);
    onPeriodReviewChange?.(years);
  };

  const isSummary = activeTab === 'summary';

  const getTitle = () => {
    if (isSummary) return 'Review Summary';
    return tabs.find(({ key }) => key === activeTab)?.title;
  };

  const handleAddItem = async () => {
    if (!fieldValue.trim()) return;

    const { sub, name } = getUserInfo();
    const createdAt = +new Date();
    const localReview = {
      id: String(createdAt),
      tab: activeTab,
      text: fieldValue,
      updateAt: createdAt,
      authorSub: sub || effectiveCurrentUserSub,
      authorName: name,
    };

    if (entityId) {
      try {
        const res = await apiService.comments.create({
          entityType,
          entityId,
          entityRevision,
          tab: localReview.tab,
          text: localReview.text,
          authorSub: localReview.authorSub,
          authorName: localReview.authorName,
        });

        if (res.data.success) {
          const saved = res.data.data;

          setReviews((prev) => [
            ...prev,
            {
              ...localReview,
              id: saved.id,
              entityType: saved.entityType,
              entityId: saved.entityId,
              entityRevision: saved.entityRevision,
              updateAt: saved.updatedAt,
              resolvedAt: saved.resolvedAt,
              frozenAt: saved.frozenAt,
            },
          ]);
          setFieldValue('');
        }
      } catch (err) {
        console.error('Failed to save comment:', err);
      }
      return;
    }

    setReviews((prev) => [...prev, localReview]);
    setFieldValue('');
  };

  const handleEdit = (id, text) => {
    setEditingId(id);
    setEditValue(text);
  };

  const handleEditSave = async (id) => {
    const nextText = editValue;

    if (savedCommentIds.has(id)) {
      try {
        await apiService.comments.update(id, { text: nextText });
      } catch (err) {
        console.error('Failed to update comment:', err);
        return;
      }
    }

    setReviews((prev) =>
      prev.map((r) =>
        r.id === id ? { ...r, text: nextText, updateAt: +new Date() } : r
      )
    );
    setEditingId(null);
    setEditValue('');
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditValue('');
  };

  const handleDelete = async (id) => {
    if (savedCommentIds.has(id)) {
      try {
        await apiService.comments.delete(id);
      } catch (err) {
        console.error('Failed to delete comment:', err);
      }
    }

    setReviews((prev) => prev.filter((r) => r.id !== id));
  };

  const handleCheckboxChanged = (value) => setCanApprove?.(value);

  const handleResolveComment = async (id) => {
    try {
      const resolvedAt = new Date();

      await apiService.comments.update(id, { resolvedAt });
      setReviews((prev) =>
        prev.map((r) => (r.id === id ? { ...r, resolvedAt } : r))
      );
    } catch (err) {
      console.error('Failed to resolve comment:', err);
    }
  };

  const handleResolveCommentConfirm = (id) => {
    showConfirmModal({
      title: 'Resolve Comment',
      description: 'Are you sure you want to resolve this comment?',
      confirmText: 'Resolve',
      onConfirm: () => handleResolveComment(id),
    });
  };

  return (
    <Card className={className}>
      <CardHeader>
        <h3 className="text-xl font-bold tracking-tight">{getTitle()}</h3>
      </CardHeader>
      <CardContent>
        <div className="mb-4">
          {(isSummary ||
            reviews.filter(({ tab }) => tab === activeTab)?.length > 0) && (
            <Card className="px-4 py-3">
              {canSubmit && (
                <div className="mb-4 font-bold">
                  <span>{userName}</span>
                  <span className="ml-1">({reviewerRole || 'Reviewer'})</span>
                </div>
              )}
              <div className="space-y-2">
                {tabs
                  .filter(({ key }) => isSummary || key === activeTab)
                  .map(({ key, title }) => (
                    <div key={key}>
                      {isSummary &&
                        reviews.filter(({ tab }) => tab === key)?.length >
                          0 && (
                          <h4 className="text-sm font-bold tracking-tight">
                            {title}
                          </h4>
                        )}
                      {reviews
                        .filter(({ tab }) => tab === key)
                        .map((review) => {
                          const { id, text, updateAt, authorName } = review;
                          const isSaved = savedCommentIds.has(id);

                          return (
                            <div
                              className="flex items-center justify-between"
                              key={id}
                            >
                              {editingId === id ? (
                                <div className="flex-1 mr-2">
                                  <Textarea
                                    className="font-mono resize-none text-sm"
                                    onChange={({ target }) =>
                                      setEditValue(target.value)
                                    }
                                    rows={2}
                                    value={editValue}
                                  />
                                  <div className="flex gap-2 mt-1 justify-end">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={handleEditCancel}
                                    >
                                      Cancel
                                    </Button>
                                    <Button
                                      size="sm"
                                      onClick={() => handleEditSave(id)}
                                    >
                                      Save
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <span className="font-semibold text-sm">
                                    {authorName}
                                  </span>
                                  <span
                                    className={`whitespace-pre-wrap ml-1 ${
                                      review.resolvedAt
                                        ? 'line-through text-neutral-400'
                                        : ''
                                    }`}
                                  >
                                    {text}
                                    {!review.frozenAt &&
                                      !review.resolvedAt && (
                                        <span className="ml-1 italic text-neutral-500">
                                          (new)
                                        </span>
                                      )}
                                  </span>
                                  <span className="ml-1 text-sm text-neutral-500">
                                    ({formatDate(updateAt)})
                                  </span>
                                </div>
                              )}
                              <div className="shrink-0 flex gap-1">
                                {canSubmit ? (
                                  <>
                                    {!isSaved && (
                                      <>
                                        <TooltipButton
                                          onClick={() => handleEdit(id, text)}
                                          size="icon"
                                          tooltip="Edit"
                                          variant="ghost"
                                        >
                                          <Edit className="h-4 w-4" />
                                        </TooltipButton>
                                        <TooltipButton
                                          onClick={() => handleDelete(id)}
                                          size="icon"
                                          tooltip="Delete"
                                          variant="ghost"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </TooltipButton>
                                      </>
                                    )}
                                    {isSaved &&
                                      !review.frozenAt &&
                                      review.authorSub ===
                                        effectiveCurrentUserSub && (
                                        <>
                                          <TooltipButton
                                            onClick={() => handleEdit(id, text)}
                                            size="icon"
                                            tooltip="Edit"
                                            variant="ghost"
                                          >
                                            <Edit className="h-4 w-4" />
                                          </TooltipButton>
                                          <TooltipButton
                                            onClick={() => handleDelete(id)}
                                            size="icon"
                                            tooltip="Delete"
                                            variant="ghost"
                                          >
                                            <Trash2 className="h-4 w-4" />
                                          </TooltipButton>
                                        </>
                                      )}
                                    {isSaved &&
                                      review.frozenAt &&
                                      !review.resolvedAt &&
                                      review.authorSub ===
                                        effectiveCurrentUserSub && (
                                        <TooltipButton
                                          onClick={() =>
                                            handleResolveCommentConfirm(id)
                                          }
                                          size="icon"
                                          tooltip="Resolve"
                                          variant="ghost"
                                        >
                                          <CheckCheck className="h-4 w-4" />
                                        </TooltipButton>
                                      )}
                                  </>
                                ) : !review.resolvedAt ? (
                                  <span className="text-xs text-muted-foreground">
                                    Unresolved
                                  </span>
                                ) : null}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  ))}
              </div>
            </Card>
          )}
        </div>
        {isSummary ? (
          <div>
            <h3 className="mb-4 text-xl font-bold tracking-tight">
              Review & Approval History
            </h3>
            <p className="text-sm text-neutral-500">
              No review or approval history available.
            </p>
            {canSubmit && (
              <>
                <div className="flex mt-4">
                  <Checkbox onChange={handleCheckboxChanged} />
                  <span className="text-sm">
                    I hereby declare that the document has been reviewed and is
                    ready for the next step of the document review.
                  </span>
                </div>
                {reviewerRole === 'QA' && isDocument && (
                  <div className="flex items-center gap-2 mt-3 ml-6">
                    <label
                      htmlFor="periodReviewYears"
                      className="text-sm text-neutral-500 whitespace-nowrap"
                    >
                      Period Review (years):
                    </label>
                    <Input
                      id="periodReviewYears"
                      type="number"
                      min="1"
                      max="20"
                      className="w-20"
                      value={periodReviewValue}
                      onChange={(e) =>
                        handlePeriodReviewChange(e.target.value)
                      }
                    />
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <div>
            {canSubmit && (
              <>
                <h4 className="mb-2 font-bold">Add Comment</h4>
                <div>
                  <Textarea
                    className="font-mono resize-none"
                    onChange={({ target }) => setFieldValue(target.value)}
                    placeholder={`Commenting...`}
                    rows={4}
                    value={fieldValue}
                  />
                </div>
                <div className="mt-4 text-right">
                  <Button onClick={handleAddItem}>Submit Comment</Button>
                </div>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CommentsPanel;
